import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pin, Trash2, X, Download, SlidersHorizontal, ListTree } from 'lucide-react';
import { aiResultsApi, AiResultKind, AiResultLite, AiResultFull } from '@/lib/aiResults';
import { forecastsApi, Driver } from '@/lib/forecasts';
import { boardPackApi } from '@/lib/boardPack';
import { qaApi, FactRow } from '@/lib/qa';
import { formatMoney } from '@/lib/currency';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownText } from '@/components/MarkdownText';

const KINDS: (AiResultKind | 'all')[] = ['all', 'qa', 'audit', 'forecast', 'board_pack'];

export function AiResultsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<AiResultKind | 'all'>('all');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const listKey = ['ai-results', wsId, kind, pinnedOnly];
  const { data: rows = [], isLoading } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      aiResultsApi.list(wsId!, { kind: kind === 'all' ? undefined : kind, pinned: pinnedOnly || undefined }),
    enabled: !!wsId,
  });

  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => aiResultsApi.pin(id, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-results', wsId] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => aiResultsApi.remove(id),
    onSuccess: () => {
      setOpenId(null);
      qc.invalidateQueries({ queryKey: ['ai-results', wsId] });
    },
  });

  function kindLabel(k: AiResultKind | 'all') {
    return t(`ai_results.kind_${k}`);
  }

  return (
    <AppShell title={t('ai_results.title')} subtitle={t('ai_results.subtitle')}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('ai_results.title')}</h1>
          <p className="mt-1 text-ink-600">{t('ai_results.subtitle')}</p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1 text-xs">
            {KINDS.map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={
                  'px-3 py-1.5 rounded-md font-medium transition ' +
                  (kind === k ? 'bg-foreground text-background' : 'text-ink-600 hover:bg-foreground/[0.04]')
                }
              >
                {kindLabel(k)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <input type="checkbox" checked={pinnedOnly} onChange={(e) => setPinnedOnly(e.target.checked)} />
            {t('ai_results.pinned_only')}
          </label>
        </div>

        <div className="rounded-md border border-border bg-white divide-y divide-border min-h-[160px]">
          {isLoading ? (
            <p className="p-5 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">{t('ai_results.empty')}</p>
          ) : (
            rows.map((r) => (
              <ResultRow
                key={r.id}
                r={r}
                t={t}
                onOpen={() => setOpenId(r.id)}
                onPin={() => pin.mutate({ id: r.id, pinned: !r.pinned })}
              />
            ))
          )}
        </div>
      </main>

      {openId && (
        <ResultDrawer
          id={openId}
          wsId={wsId!}
          t={t}
          onClose={() => setOpenId(null)}
          onDelete={() => remove.mutate(openId)}
          deleting={remove.isPending}
        />
      )}
    </AppShell>
  );
}

function ResultRow({
  r,
  t,
  onOpen,
  onPin,
}: {
  r: AiResultLite;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onOpen: () => void;
  onPin: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-foreground/[0.02]">
      <button
        onClick={onPin}
        title={t(r.pinned ? 'ai_results.unpin' : 'ai_results.pin')}
        className={'shrink-0 p-1 rounded ' + (r.pinned ? 'text-emerald-600' : 'text-ink-300 hover:text-ink-600')}
      >
        <Pin className="h-4 w-4" fill={r.pinned ? 'currentColor' : 'none'} strokeWidth={1.8} />
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-start">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.14em] pill-emerald rounded px-1.5 py-0.5">{t(`ai_results.kind_${r.kind}`)}</span>
          <span className="text-sm font-medium text-foreground truncate">{r.title || '—'}</span>
        </div>
        <div className="num text-[11px] text-muted-foreground mt-0.5">
          {new Date(r.createdAt).toLocaleString()}
          {r.citations > 0 && <> · {t('ai_results.citations', { count: r.citations })}</>}
          {r.costCents != null && <> · ${(r.costCents / 100).toFixed(4)}</>}
        </div>
      </button>
    </div>
  );
}

function ResultDrawer({
  id,
  wsId,
  t,
  onClose,
  onDelete,
  deleting,
}: {
  id: string;
  wsId: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onClose: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { data, isLoading } = useQuery({ queryKey: ['ai-result', id], queryFn: () => aiResultsApi.get(id) });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" aria-label={t('qa.close')} />
      <aside className="relative w-full max-w-lg h-full bg-background border-l border-border shadow-xl flex flex-col">
        <header className="flex items-center justify-between px-5 h-14 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground truncate pe-2">{data?.title || t('ai_results.title')}</h3>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onDelete} disabled={deleting} className="text-xs text-warn-700 hover:bg-warn-500/10 px-2 py-1 rounded-md inline-flex items-center gap-1 disabled:opacity-50">
              <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
            </button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-foreground/[0.04]">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isLoading || !data ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <>
              <ResultBody result={data} t={t} />
              <ResultActions result={data} wsId={wsId} t={t} onClose={onClose} />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function ResultBody({ result, t }: { result: AiResultFull; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const out = result.output as Record<string, unknown>;
  if (typeof out.reply === 'string') return <MarkdownText>{out.reply}</MarkdownText>;
  if (Array.isArray(out.sections)) {
    return (
      <div className="space-y-4">
        {(out.sections as { heading: string; body: string }[]).map((s, i) => (
          <div key={i}>
            <h4 className="text-sm font-semibold text-foreground">{s.heading}</h4>
            <div className="mt-1 text-sm text-ink-700"><MarkdownText>{s.body}</MarkdownText></div>
          </div>
        ))}
      </div>
    );
  }
  if (Array.isArray(out.suggestions)) {
    return (
      <div className="space-y-2">
        {(out.suggestions as { name: string; rationale?: string }[]).map((d, i) => (
          <div key={i} className="rounded-md border border-border p-3">
            <div className="text-sm font-medium text-foreground">{d.name}</div>
            {d.rationale && <div className="text-[12px] text-muted-foreground mt-0.5">{d.rationale}</div>}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-[11px] text-ink-700 whitespace-pre-wrap break-words">{JSON.stringify(out, null, 2)}</pre>;
}

/** Turns a saved result into action — the gap between "view" and "use". */
function ResultActions({
  result, wsId, t, onClose,
}: {
  result: AiResultFull;
  wsId: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [showFacts, setShowFacts] = useState(false);
  const out = result.output as Record<string, unknown>;
  const input = result.input as Record<string, unknown>;

  const createForecast = useMutation({
    mutationFn: () =>
      forecastsApi.create(wsId, {
        scenarioLabel: (result.title || 'Forecast from AI').slice(0, 180),
        drivers: (out.suggestions ?? []) as Driver[],
        aiSuggestions: (out.suggestions ?? []) as Driver[],
      }),
  });

  const downloadPdf = useMutation({
    mutationFn: async () => {
      const blob = await boardPackApi.downloadPdf(wsId, {
        audience: (input.audience as 'board' | 'cfo' | 'investor') ?? 'board',
        periodStart: String(input.periodStart ?? ''),
        periodEnd: String(input.periodEnd ?? ''),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cadencez-board-pack.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });

  const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60';

  const hasForecast = result.kind === 'forecast' && Array.isArray(out.suggestions) && out.suggestions.length > 0;
  const hasBoardPack = result.kind === 'board_pack';
  const hasFacts = (result.kind === 'qa' || result.kind === 'audit') && result.citedFactIds.length > 0;
  if (!hasForecast && !hasBoardPack && !hasFacts) return null;

  return (
    <div className="border-t border-border pt-4 space-y-3">
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">{t('ai_results.actions')}</p>

      {hasForecast && (
        <div className="flex flex-wrap items-center gap-2">
          {!createForecast.isSuccess ? (
            <button onClick={() => createForecast.mutate()} disabled={createForecast.isPending} className={btn}>
              <SlidersHorizontal className="h-4 w-4" />
              {createForecast.isPending ? t('forecast.saving') : t('ai_results.apply_forecast')}
            </button>
          ) : (
            <>
              <span className="text-xs text-emerald-700 font-semibold">{t('ai_results.applied')}</span>
              <button onClick={() => { onClose(); navigate(`/app/workspace/${wsId}/planning?forecast=${createForecast.data?.id ?? ''}`); }} className="px-3 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-foreground/[0.04]">
                {t('ai_results.open_planning')}
              </button>
              {createForecast.data && (
                <button onClick={() => { onClose(); navigate(`/app/workspace/${wsId}/approvals?targetKind=forecast&targetId=${createForecast.data!.id}`); }} className="px-3 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-foreground/[0.04]">
                  {t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
                </button>
              )}
            </>
          )}
          {createForecast.isError && <span className="text-xs text-warn-700">{(createForecast.error as Error).message}</span>}
        </div>
      )}

      {hasBoardPack && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => downloadPdf.mutate()} disabled={downloadPdf.isPending} className={btn}>
            <Download className="h-4 w-4" />
            {downloadPdf.isPending ? t('ai_results.downloading') : t('ai_results.download_pdf')}
          </button>
          <button
            onClick={() => { onClose(); navigate(`/app/workspace/${wsId}/approvals?targetKind=board_pack&targetId=${encodeURIComponent(`${(input.audience as string) ?? 'board'} ${String(input.periodStart ?? '')}→${String(input.periodEnd ?? '')}`)}`); }}
            className="px-3 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-foreground/[0.04]"
          >
            {t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
          </button>
        </div>
      )}

      {hasFacts && (
        <div>
          <button onClick={() => setShowFacts((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-foreground/[0.04]">
            <ListTree className="h-4 w-4" />
            {t('ai_results.view_facts', { count: result.citedFactIds.length })}
          </button>
          {showFacts && <CitedFacts wsId={wsId} ids={result.citedFactIds} t={t} />}
        </div>
      )}
    </div>
  );
}

function CitedFacts({ wsId, ids, t }: { wsId: string; ids: string[]; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const { data: facts = [], isLoading } = useQuery({
    queryKey: ['facts', wsId, ids.join(',')],
    queryFn: () => qaApi.facts(wsId, ids),
  });
  if (isLoading) return <p className="mt-3 text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (facts.length === 0) return <p className="mt-3 text-sm text-muted-foreground">—</p>;
  return (
    <div className="mt-3 space-y-2">
      {facts.map((f: FactRow) => (
        <div key={f.factId} className="rounded-md border border-border p-3">
          <div className="flex items-baseline justify-between">
            <span className="num text-sm font-semibold text-foreground">{f.accountCode}</span>
            <span className="num text-sm tabular-nums text-foreground">{formatMoney(f.amount, { maximumFractionDigits: 0 })}</span>
          </div>
          <div className="num text-[11px] text-muted-foreground">#{f.factId} · {f.scenario} · {f.periodStart} → {f.periodEnd}</div>
        </div>
      ))}
    </div>
  );
}
